import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Spinner,
  Center,
  useToast,
  Badge,
  IconButton,
  Text,
  Flex,
  Switch,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { EditIcon, DeleteIcon, AddIcon } from "@chakra-ui/icons";
import { supabase } from "../../../lib/supabaseClient";

const PopupList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [homePopupEnabled, setHomePopupEnabled] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    fetchPopups();
    fetchHomePopupSetting();
  }, []);

  const fetchPopups = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("popup")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPopups(data || []);
    } catch (error) {
      console.error("팝업 조회 실패:", error);
      toast({
        title: "팝업을 불러오는데 실패했습니다",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (popupId) => {
    navigate(`/admin/popup/edit/${popupId}`);
  };

  const handleDelete = async (popupId) => {
    if (!window.confirm("정말로 이 팝업을 삭제하시겠습니까?")) return;

    try {
      const { error } = await supabase.from("popup").delete().eq("id", popupId);

      if (error) throw error;

      toast({
        title: "팝업이 삭제되었습니다",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      fetchPopups();
    } catch (error) {
      console.error("팝업 삭제 실패:", error);
      toast({
        title: "팝업 삭제에 실패했습니다",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchHomePopupSetting = async () => {
    try {
      const { data, error } = await supabase
        .from("settings")
        .select("setting_switch")
        .eq("setting_name", "home_popup")
        .single();

      if (error) throw error;
      setHomePopupEnabled(data?.setting_switch || false);
    } catch (error) {
      console.error("홈팝업 설정 조회 실패:", error);
    }
  };

  const handleHomePopupToggle = async () => {
    try {
      setSettingsLoading(true);
      const newValue = !homePopupEnabled;

      const { error } = await supabase
        .from("settings")
        .update({ setting_switch: newValue })
        .eq("setting_name", "home_popup");

      if (error) throw error;

      setHomePopupEnabled(newValue);
      toast({
        title: `홈팝업이 ${newValue ? "활성화" : "비활성화"}되었습니다`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("홈팝업 설정 변경 실패:", error);
      toast({
        title: "설정 변경에 실패했습니다",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" p={6}>
      <Container maxW="container.xl">
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg" color="gray.800">
            팝업 관리
          </Heading>
          <HStack spacing={4}>
            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel
                htmlFor="home-popup-toggle"
                mb="0"
                fontSize="sm"
                color="gray.700"
              >
                홈팝업 표시
              </FormLabel>
              <Switch
                id="home-popup-toggle"
                isChecked={homePopupEnabled}
                onChange={handleHomePopupToggle}
                isDisabled={settingsLoading}
                colorScheme="blue"
                size="md"
              />
            </FormControl>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              onClick={() => navigate("/admin/popup/create")}
            >
              새 팝업 등록
            </Button>
          </HStack>
        </Flex>

        <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>제목</Th>
                <Th>상태</Th>
                <Th>이미지</Th>
                <Th>페이지 URL</Th>
                <Th>등록일</Th>
                <Th>작업</Th>
              </Tr>
            </Thead>
            <Tbody>
              {popups.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={8}>
                    <Text color="gray.500">등록된 팝업이 없습니다</Text>
                  </Td>
                </Tr>
              ) : (
                popups.map((popup) => (
                  <Tr key={popup.id} _hover={{ bg: "gray.50" }}>
                    <Td>
                      <Text fontWeight="medium" noOfLines={1}>
                        {popup.title}
                      </Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={popup.is_visible ? "green" : "gray"}
                        variant="subtle"
                      >
                        {popup.is_visible ? "표시" : "숨김"}
                      </Badge>
                    </Td>
                    <Td>
                      {popup.img_url ? (
                        <Text fontSize="sm" color="blue.600">
                          이미지 있음
                        </Text>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          이미지 없음
                        </Text>
                      )}
                    </Td>
                    <Td>
                      {popup.page_url ? (
                        <Text fontSize="sm" color="blue.600" noOfLines={1}>
                          {popup.page_url}
                        </Text>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          링크 없음
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Text fontSize="sm" color="gray.600">
                        {formatDate(popup.created_at)}
                      </Text>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<EditIcon />}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleEdit(popup.id)}
                          aria-label="수정"
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDelete(popup.id)}
                          aria-label="삭제"
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      </Container>
    </Box>
  );
};

export default PopupList;
